import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, httpClient, AuthApiError } from '@/api/http-client';
import { useAuth } from '@/hooks';
import { Loader2 } from 'lucide-react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: number | string;
              locale?: string;
            },
          ) => void;
          prompt?: () => void;
        };
      };
    };
  }
}

interface GoogleLoginButtonProps {
  mode?: 'login' | 'register';
  onError?: (errorMessage: string) => void;
  className?: string;
}

export const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  mode = 'login',
  onError,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectParam = searchParams.get('redirect') || searchParams.get('returnUrl');
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  const handleCredentialResponse = async (response: { credential: string }) => {
    if (!response?.credential) return;

    setIsLoading(true);
    try {
      const authRes = await authApi.googleLogin(response.credential);

      if (authRes?.accessToken) {
        httpClient.saveAccessToken(authRes.accessToken);
        await login(authRes.user?.email || '', '', authRes);

        const targetPath =
          redirectParam && redirectParam.startsWith('/')
            ? redirectParam
            : '/dashboard';

        navigate(targetPath, { replace: true });
      }
    } catch (err: any) {
      console.error('[GoogleLogin] Erro ao autenticar:', err);
      let message = 'Não foi possível autenticar com o Google. Tente novamente.';
      if (err instanceof AuthApiError) {
        message = err.message;
      } else if (err?.message) {
        message = err.message;
      }
      if (onError) {
        onError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!googleClientId) {
      return;
    }

    let isMounted = true;

    const initGsi = () => {
      if (!isMounted || !window.google?.accounts?.id || !containerRef.current) return;

      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Limpa filhos anteriores caso re-renderize
        containerRef.current.innerHTML = '';

        window.google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          text: mode === 'register' ? 'signup_with' : 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: containerRef.current.offsetWidth || 340,
          locale: 'pt-BR',
        });
      } catch (err) {
        console.error('[GoogleLogin] Erro ao inicializar GIS:', err);
      }
    };

    if (!window.google?.accounts?.id) {
      const scriptId = 'google-gsi-client-script';
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;

      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => initGsi();
        document.head.appendChild(script);
      } else {
        const interval = setInterval(() => {
          if (window.google?.accounts?.id) {
            clearInterval(interval);
            initGsi();
          }
        }, 100);
        return () => clearInterval(interval);
      }
    } else {
      initGsi();
    }

    return () => {
      isMounted = false;
    };
  }, [googleClientId, mode]);

  // Se o GOOGLE_CLIENT_ID não estiver configurado no .env, não renderiza o botão
  if (!googleClientId) {
    return null;
  }

  return (
    <div className={`w-full flex flex-col items-center my-2 ${className}`}>
      {isLoading ? (
        <div className="w-full flex items-center justify-center py-2.5 px-4 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-sm font-medium text-neutral-600 dark:text-neutral-300 gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span>Autenticando com o Google...</span>
        </div>
      ) : (
        <div ref={containerRef} className="w-full min-h-[44px] flex justify-center" />
      )}
    </div>
  );
};
